/**
 * Reports > On Order (/reports/on-order).
 *
 * Every line on an open purchase order, with what has not arrived yet: summary tiles
 * (open purchase orders, units on order, open cost, lines past their cancel date) and
 * one sortable table with the location, ship-to, vendor, PO number, status, product
 * statement, item, description, quantity ordered and received, the cost still open,
 * and the order, due and cancel dates. Replaces the QuickSight dashboard "Product On
 * Order".
 *
 * This page is deliberately thin. The screen itself is the shared `OnOrder` component
 * in `@ampersandbrands/ui` (presentational, so The Sweet Spot can reuse it); this file
 * fetches through `useOnOrder`, keeps the filters and the sort in the URL so a view is
 * a shareable link, and adds the page chrome: header with the pin toggle, the daily
 * email control, and the Excel download (every matching line).
 *
 * RBAC: `reports.onorder` sees the whole fleet. A store manager (holding
 * `stores.sweetspot`) reaches an own-store view, enforced server-side like OMNI
 * Performance, so the store picker is hidden when the api reports store scope.
 *
 * Resilience: wrapped in an ErrorBoundary and degrades to a quiet "not available yet"
 * card on 404/503 while the api ships.
 */

import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { OnOrder as OnOrderScreen, type OnOrderFilters, type OnOrderSort } from '@ampersandbrands/ui';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ReportSubscribeControl } from '@/components/ReportSubscribeControl';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { useSession } from '@/lib/useSession';
import { useReportPageState } from '@/lib/reportPageState';
import { isNotAvailable } from '@/lib/useStores';
import { ON_ORDER_REPORT_TYPE } from '@/lib/useReportSubscriptions';
import {
  downloadOnOrderXlsx,
  onOrderSubscriptionFilters,
  parseOnOrderFilters,
  parseOnOrderSort,
  useOnOrder,
  writeOnOrderFilters,
  writeOnOrderSort,
} from '@/lib/useOnOrder';
import { hasAccessSlug } from '@/lib/toolVisibility';
import { AccessDeniedCard, ErrorCard, NotAvailableCard, StoresHeader } from '../stores/storesShared';

/** The accessSlug that grants this report (fleet view). */
const SLUG = 'reports.onorder';

/**
 * Fleet through `reports.onorder`, or an own-store view through `stores.sweetspot`
 * (the same dual audience as OMNI Performance). The api enforces the real scope; this
 * only decides whether to show the page.
 */
function canViewOnOrder(perms: string[] | undefined, superAdmin = false): boolean {
  if (hasAccessSlug(perms, SLUG, superAdmin)) return true;
  return !!perms?.includes('stores.sweetspot');
}

function OnOrderInner() {
  const { user } = useSession();
  const canView = canViewOnOrder(user?.permissions, user?.superAdmin);

  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseOnOrderFilters(searchParams), [searchParams]);
  const sort = useMemo(() => parseOnOrderSort(searchParams), [searchParams]);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const setFilters = useCallback(
    (next: OnOrderFilters) =>
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          writeOnOrderFilters(sp, next);
          return sp;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  const setSort = useCallback(
    (next: OnOrderSort) =>
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          writeOnOrderSort(sp, next);
          return sp;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  const dataQ = useOnOrder(filters, sort, canView);
  // The api lists the locations that have open orders, named from retail_stores (a
  // warehouse takes its ship-to name). A store-scoped caller sees one store, so the
  // picker would only mislead.
  const storeOptions = dataQ.scope === 'store' ? [] : dataQ.locations;

  const pageState = useMemo(
    () => [
      { label: 'Stores', value: filters.store_no.length > 0 ? filters.store_no.join(', ') : 'All stores' },
      { label: 'Vendors', value: filters.vendor.length > 0 ? filters.vendor.join(', ') : 'All vendors' },
      { label: 'PO status', value: filters.status.length > 0 ? filters.status.join(', ') : 'All statuses' },
      ...(filters.q ? [{ label: 'Search', value: filters.q }] : []),
      ...(filters.past_cancel ? [{ label: 'Past cancel date only', value: 'Yes' }] : []),
      ...(sort.col ? [{ label: 'Sort', value: `${sort.col} ${sort.dir}` }] : []),
    ],
    [filters, sort],
  );
  useReportPageState(pageState);

  async function handleExport() {
    setExporting(true);
    setToast(null);
    try {
      await downloadOnOrderXlsx(filters, sort);
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setExporting(false);
    }
  }

  if (user && !canView) return <AccessDeniedCard what="On Order" />;

  const header = (
    <StoresHeader
      eyebrow="Reports"
      title="On Order"
      subtitle="Every line on an open purchase order and what has not arrived yet: quantity ordered and received, the cost still open, and the order, due, and cancel dates. A line past its cancel date is highlighted. Refreshed from CounterPoint every few minutes."
      actions={<ToolPinToggle toolSlug="on-order" toolName="On Order" />}
    />
  );

  if (isNotAvailable(dataQ.error)) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        {header}
        <NotAvailableCard what="On Order" />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {header}
      {toast && (
        <p role="alert" className="mb-3 text-sm text-red-700 dark:text-red-300">
          {toast}
        </p>
      )}
      <OnOrderScreen
        rows={dataQ.rows}
        totalRows={dataQ.totalRows}
        summary={dataQ.summary}
        storeOptions={storeOptions}
        vendorOptions={dataQ.vendors}
        statusOptions={dataQ.statuses}
        asOf={dataQ.asOf}
        loading={dataQ.isLoading}
        error={dataQ.error ? <ErrorCard message={dataQ.error.message} /> : undefined}
        filters={filters}
        onFiltersChange={setFilters}
        sort={sort}
        onSortChange={setSort}
        onExport={() => void handleExport()}
        exporting={exporting}
        toolbarExtra={
          <ReportSubscribeControl
            reportType={ON_ORDER_REPORT_TYPE}
            filters={onOrderSubscriptionFilters(filters)}
            subscribeHint="Get this report by email every morning, saving the store, vendor, status, search, and past-cancel-date filters you have applied."
          />
        }
      />
    </div>
  );
}

export function OnOrder() {
  return (
    <ErrorBoundary>
      <OnOrderInner />
    </ErrorBoundary>
  );
}
